import type { Question } from '@titularis/api-client';

/**
 * Copia a questão para a área de transferência, para colar em outra plataforma.
 *
 * **Vai nos dois formatos ao mesmo tempo.** Colado num editor rico - Word,
 * Google Docs, outro backoffice - entra com a formatação preservada; colado num
 * campo simples, entra como texto legível. Escrever só HTML deixaria marcação
 * crua em qualquer campo de texto; só texto jogaria fora negrito, lista e
 * tabela, que é justamente o trabalho que se está tentando reaproveitar.
 */
type Versao = NonNullable<Question['latestVersion']>;

const LETRA = (letra: string) => letra.toUpperCase();

const ABAS: { chave: 'legislation' | 'jurisprudence' | 'doctrine'; titulo: string }[] = [
  { chave: 'legislation', titulo: 'Legislação' },
  { chave: 'jurisprudence', titulo: 'Jurisprudência' },
  { chave: 'doctrine', titulo: 'Doutrina' },
];

/**
 * HTML para texto, preservando a quebra de blocos.
 *
 * Feito com o parser do navegador, e não com regex: o HTML vem do nosso próprio
 * render, mas tirar marcação com expressão regular erra em aninhamento e em
 * entidade, e o resultado colado no lugar errado é lixo silencioso.
 */
function comoTexto(html: string | null | undefined): string {
  if (!html) return '';

  const doc = new DOMParser().parseFromString(html, 'text/html');
  // `<br>` e fim de bloco viram quebra: sem isso, lista e parágrafo colam tudo
  // numa linha só.
  doc.querySelectorAll('br').forEach((quebra) => quebra.replaceWith('\n'));
  doc.querySelectorAll('p, li, tr, h1, h2, h3, h4, blockquote').forEach((bloco) => {
    bloco.append('\n');
  });

  return (doc.body.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
}

export interface OpcoesDaCopia {
  /** Sem isto, sai só enunciado e alternativas - o que se cola numa prova. */
  comComentarios: boolean;
}

function montarHtml(versao: Versao, opcoes: OpcoesDaCopia): string {
  const partes: string[] = [];

  if (versao.statementHtml) partes.push(versao.statementHtml);

  for (const alternativa of versao.options ?? []) {
    // A letra entra no texto, e não como lista numerada: colada fora daqui, a
    // numeração automática do editor de destino recomeçaria do zero.
    partes.push(`<p><strong>${LETRA(alternativa.letter)})</strong> ${alternativa.html}</p>`);

    if (opcoes.comComentarios && alternativa.commentaryHtml) {
      partes.push(
        `<p><em>Comentário do item ${LETRA(alternativa.letter)}:</em></p>${alternativa.commentaryHtml}`,
      );
    }
  }

  if (versao.correctOption) {
    partes.push(`<p><strong>Gabarito:</strong> ${LETRA(versao.correctOption)}</p>`);
  }

  if (opcoes.comComentarios) {
    if (versao.answerKeyHtml) partes.push(`<p><strong>Espelho da banca</strong></p>${versao.answerKeyHtml}`);
    if (versao.answerModelHtml) {
      partes.push(`<p><strong>Modelo de Resposta</strong></p>${versao.answerModelHtml}`);
    }
    for (const aba of ABAS) {
      const conteudo = versao.commentary?.[aba.chave]?.html;
      if (conteudo) partes.push(`<p><strong>${aba.titulo}</strong></p>${conteudo}`);
    }
  }

  return partes.join('\n');
}

/**
 * Escreve nos dois formatos, com queda para texto puro.
 *
 * `ClipboardItem` não existe em todo navegador; onde faltar, ainda se copia o
 * texto - meia cópia é melhor que um botão que não faz nada.
 */
export async function copiarQuestao(versao: Versao, opcoes: OpcoesDaCopia): Promise<void> {
  const html = montarHtml(versao, opcoes);
  const texto = comoTexto(html);

  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([texto], { type: 'text/plain' }),
      }),
    ]);
    return;
  }

  await navigator.clipboard.writeText(texto);
}
